/**
 * @name puiCatalogos.ts
 * @version 1.0.0
 * @description Catálogos PUI para mostrar claves válidas en el validador
 * @author
 * @changelog
 * 1.0.0 - 2026/08/19 - Modal de catálogo en errores de validación PUI
 */
import { NOMBRE_NIVEL_ESTUDIO } from '@shared/types/enums'
import type { ICarreraPUI } from '@shared/types/pui/base'
import {
	CAptitudesSobresalientes,
	CBajaAcademica,
	CConfirmacion,
	CDiscapacidad,
	CDuracionPeriodo,
	CEntidadFederativa,
	CFlujo,
	CGenderPUI,
	CGradoPlanEstudio,
	CIdiomas,
	CModalidadEducativa,
	CNacionalidad,
	CNEcesidadEducativa,
	COrigenEstudios,
	CPeriodo,
	CRelacionOpcionModalidad,
	CTipoAntecedente,
	CTipoBajaAcademica,
	CURP_CODIGO_ENTIDAD_FEDERATIVA,
	NOMBRE_PAIS
} from '@shared/types/pui/enums'

export interface IEntradaCatalogoPui {
	clave: string
	significado: string
}

export interface ICatalogoPui {
	titulo: string
	entradas: IEntradaCatalogoPui[]
}

export interface IOpcionesCatalogoPui {
	message?: string
	claveInstitucion?: string
	carreras?: ICarreraPUI[]
}

const ETIQUETAS_CAMPO: Record<string, string> = {
	flujo: 'Flujo',
	Flujo: 'Flujo',
	'Flujo de inscripción': 'Flujo de inscripción',
	'Flujo de reinscripción': 'Flujo de reinscripción',
	anioEscolar: 'Año escolar',
	nombre: 'Nombre',
	primerApellido: 'Primer apellido',
	segundoApellido: 'Segundo apellido',
	genero: 'Sexo',
	curp: 'CURP',
	segmentoRaiz: 'Segmento raíz',
	fechaNacimiento: 'Fecha de nacimiento',
	correoInstitucional: 'Correo institucional',
	correoElectronico: 'Correo electrónico',
	telefono: 'Teléfono',
	nacionalidad: 'Nacionalidad',
	paisNacimiento: 'País de nacimiento',
	paisResidencia: 'País de residencia',
	paisProcedencia: 'País de procedencia',
	'Entidad de nacimiento': 'Entidad de nacimiento',
	'Entidad de residencia': 'Entidad de residencia',
	'Entidad de procedencia': 'Entidad de procedencia',
	idioma: 'Lengua',
	segundoIdioma: 'Segundo idioma',
	necesidadesEspeciales: 'Necesidad educativa',
	tipoDiscapacidad: 'Tipo de discapacidad',
	aptitudSobresaliente: 'Aptitud sobresaliente',
	claveInstitucion: 'Clave de institución',
	'Clave de la escuela': 'Clave de la escuela (CCT)',
	claveCarrera: 'Clave de carrera',
	creditosTotales: 'Créditos totales',
	creditosObtenidos: 'Créditos obtenidos',
	nivelEducativo: 'Nivel educativo',
	modalidadEducativa: 'Modalidad educativa',
	opcionEducativa: 'Opción educativa',
	numeroAcuerdoRvoe: 'Número de acuerdo RVOE',
	fechaAcuerdoRvoe: 'Fecha de acuerdo RVOE',
	cicloEscolar: 'Ciclo escolar',
	gradoAvance: 'Grado de avance',
	periodo: 'Periodo',
	duracionPeriodo: 'Duración del periodo',
	fechaInicioPeriodo: 'Fecha de inicio de periodo',
	antecedenteAcademico: 'Antecedente académico',
	tipoAntecedenteAcademico: 'Tipo de antecedente académico',
	matriculaInstitucional: 'Matrícula institucional',
	fechaInicioEstudios: 'Fecha de inicio de estudios',
	origenEstudios: 'Origen de estudios',
	tipoBajaAcademica: 'Tipo de baja académica',
	motivoBaja: 'Motivo de baja',
	general: 'General'
}

function entradasDesdeRecord(catalogo: Record<string | number, string>): IEntradaCatalogoPui[] {
	return Object.entries(catalogo).map(([clave, significado]) => ({ clave, significado }))
}

function catalogo(titulo: string, catalogoRecord: Record<string | number, string>): ICatalogoPui {
	return { titulo, entradas: entradasDesdeRecord(catalogoRecord) }
}

function catalogoEntidadCurp(): ICatalogoPui {
	return {
		titulo: 'Entidad federativa (CURP)',
		entradas: Object.entries(CURP_CODIGO_ENTIDAD_FEDERATIVA).map(([clave, entidad]) => ({
			clave,
			significado: CEntidadFederativa[entidad] ?? clave
		}))
	}
}

function catalogoCarreras(carreras?: ICarreraPUI[]): ICatalogoPui {
	return {
		titulo: 'Clave de carrera',
		entradas: (carreras ?? []).map(carrera => {
			const rvoes = (carrera.numeroAcuerdoRvoe ?? []).map(item => item.numeroAcuerdoRvoe).filter(Boolean)
			return {
				clave: carrera.claveCarrera,
				significado: rvoes.length > 0 ? `RVOE: ${rvoes.join(', ')}` : 'Carrera de la institución'
			}
		})
	}
}

function resolverClaveCatalogo(field: string, message?: string): string {
	const msg = (message ?? '').toLowerCase()
	if (field === 'curp' || field === 'segmentoRaiz') {
		if (msg.includes('género') || msg.includes('genero')) return 'genero'
		if (msg.includes('entidad')) return 'entidadCurp'
	}
	if (field === 'Flujo de inscripción' || field === 'Flujo de reinscripción' || field === 'Flujo') {
		return field === 'Flujo' && msg.includes('antecedente') ? 'tipoAntecedenteAcademico' : 'flujo'
	}
	return field
}

const CATALOGOS_POR_CAMPO: Record<string, () => ICatalogoPui> = {
	flujo: () => catalogo('Flujo', CFlujo),
	genero: () => catalogo('Sexo', CGenderPUI),
	nacionalidad: () => catalogo('Nacionalidad', CNacionalidad),
	paisNacimiento: () => catalogo('País de nacimiento', NOMBRE_PAIS),
	paisResidencia: () => catalogo('País de residencia', NOMBRE_PAIS),
	paisProcedencia: () => catalogo('País de procedencia', NOMBRE_PAIS),
	'Entidad de nacimiento': () => catalogo('Entidad de nacimiento', CEntidadFederativa),
	'Entidad de residencia': () => catalogo('Entidad de residencia', CEntidadFederativa),
	'Entidad de procedencia': () => catalogo('Entidad de procedencia', CEntidadFederativa),
	entidadCurp: catalogoEntidadCurp,
	idioma: () => catalogo('Lengua', CIdiomas),
	segundoIdioma: () => catalogo('Segundo idioma', CIdiomas),
	necesidadesEspeciales: () => catalogo('Necesidad educativa', CNEcesidadEducativa),
	tipoDiscapacidad: () => catalogo('Tipo de discapacidad', CDiscapacidad),
	aptitudSobresaliente: () => catalogo('Aptitud sobresaliente', CAptitudesSobresalientes),
	nivelEducativo: () => catalogo('Nivel educativo', NOMBRE_NIVEL_ESTUDIO),
	modalidadEducativa: () => catalogo('Modalidad educativa', CModalidadEducativa),
	opcionEducativa: () =>
		catalogo(
			'Opción educativa',
			Object.fromEntries(
				Object.entries(CRelacionOpcionModalidad).flatMap(([modalidad, opciones]) => opciones.map(opcion => [opcion, CModalidadEducativa[modalidad as keyof typeof CModalidadEducativa]]))
			)
		),
	gradoAvance: () => catalogo('Grado de avance', CGradoPlanEstudio),
	periodo: () => catalogo('Periodo', CPeriodo),
	duracionPeriodo: () => catalogo('Duración del periodo', CDuracionPeriodo),
	antecedenteAcademico: () => catalogo('Antecedente académico', CConfirmacion),
	tipoAntecedenteAcademico: () => catalogo('Tipo de antecedente académico', CTipoAntecedente),
	origenEstudios: () => catalogo('Origen de estudios', COrigenEstudios),
	tipoBajaAcademica: () => catalogo('Tipo de baja académica', CBajaAcademica),
	motivoBaja: () => catalogo('Motivo de baja', CTipoBajaAcademica)
}

export function getEtiquetaCampoPui(field: string): string {
	return ETIQUETAS_CAMPO[field] ?? field
}

export function getCatalogoPui(field: string, opciones: IOpcionesCatalogoPui = {}): ICatalogoPui | null {
	const clave = resolverClaveCatalogo(field, opciones.message)

	if (clave === 'claveInstitucion') {
		const claveInstitucion = opciones.claveInstitucion?.trim()
		if (!claveInstitucion) return null
		return {
			titulo: 'Clave de institución',
			entradas: [{ clave: claveInstitucion, significado: 'Clave de la institución seleccionada' }]
		}
	}

	if (clave === 'claveCarrera') {
		const catalogoCarrera = catalogoCarreras(opciones.carreras)
		return catalogoCarrera.entradas.length > 0 ? catalogoCarrera : null
	}

	const factory = CATALOGOS_POR_CAMPO[clave]
	return factory ? factory() : null
}
